<template>
  <OverlayLoader :show="loadModal" />

  <div class="container-fluid px-4">
    <BaseTable heading="Availability" :rows="availabilityList" :columns="columns" searchable sortable
      :selectable="false" :loading="loading" :pagination="pagination" @changePage="onChangePage"
      @updatePageSize="onUpdatePageSize" @searchChanged="onSearchChanged">
      <template #buttons>
        <button class="btn btn-sm btn-info" @click="addAvailability()">
          <i class="material-icons text-lg text-white">add</i>
          Add New Availability
        </button>
      </template>

      <template #col-recurrence="{ row }">
        <ul class="list-disc pl-4">
          <li v-for="(line, idx) in getRecurrenceLines(row)" :key="idx">
            {{ sanitizeLine(line) }}
          </li>
        </ul>
      </template>

      <template #actions="{ row }">
        <button class="btn btn-sm my-0 py-0" @click="editAvailability(row.availabilityId)">
          <i class="material-icons text-lg text-orange">edit</i>
        </button>
        <button class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
          <i class="material-icons text-lg text-danger">delete</i>
        </button>
      </template>
    </BaseTable>

    <DeleteModal v-model:show="showDeleteModal" :itemName="selectedSkillName" @confirm="deleteSkill" />

    <ContentModal v-model="showModal" :title="isEditMode ? 'Edit Availability' : 'Add New Availability'" @update:modelValue="onModalClose">
      <template #default>
        <AvailabilityForm ref="availabilityForm" :personId="getPersonId()" :details="store.getters.availabilityDetails?.data?.availability" @saved="onSaved"
          @save-error="onSaveError" @error="onSaveError" />
      </template>

      <template #footer>
        <MaterialButton type="button" variant="primary" class="px-4 my-0" :disabled="isSaving" @click="handleSave">
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="material-icons text-lg me-1">save</i>
          {{ isSaving ? 'Saving...' : 'Save' }}
        </MaterialButton>
      </template>
    </ContentModal>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";

import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import AvailabilityForm from "./AvailabilityForm.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import OverlayLoader from "@/components/Loader/OverlayLoader.vue";

import { getLocationId, getLogginedPersonId } from "@/utils/SessionDataHandler";

const props = defineProps({
  personId: { type: [Number, String], default: null },
});

const store = useStore();
const route = useRoute();
const toast = useToast();
const injectedPeopleGroupId = inject('editUserPeopleGroupId', null);
const getPeopleGroupId = () =>
  injectedPeopleGroupId?.value ?? null;

// Get personId from prop (e.g. volunteer page), inject (modal), route params, or session data
const _injectedPersonId = inject('editUserPersonId', null);
const getPersonId = () => {
  if (props.personId != null && props.personId !== "") {
    return Number(props.personId);
  }
  if (_injectedPersonId?.value) {
    return Number(_injectedPersonId.value);
  }
  if (route.params.userid) {
    return Number(route.params.userid);
  }
  const sessionPersonId = getLogginedPersonId() || store.getters.stSessionData?.personId || store.getters.stSessionData?.personid;
  return sessionPersonId ? Number(sessionPersonId) : null;
};

const showModal = ref(false);
const isEditMode = computed(() => !!store.getters.availabilityDetails?.data?.availability);
const showDeleteModal = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const selectedSkill = ref(null);
const availabilityForm = ref(null);
const loadModal = ref(false);

const loading = ref(true);
const searchTerm = ref("");

const availabilityList = computed(() => store.getters.availability || []);
const pagination = computed(() => store.getters.availabilityPagination);

const columns = [
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
  { key: "recurrence", label: "Recurrence" },
  { key: "available", label: "Available" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "lastEditDate", label: "Last Edit Date" },
];

// ✅ Add availability modal
function addAvailability() {
  showModal.value = true;
}

// ✅ Handle modal close - reset form and clear store state
function onModalClose(isOpen) {
  if (!isOpen) {
    // Modal is closing, reset the form
    if (availabilityForm.value) {
      availabilityForm.value.resetForm();
    }
    // Clear the store state to ensure fresh data on next edit
    store.commit('setAvailabilityDetails', null);
  }
}

// ✅ Handle save
async function handleSave() {
  if (!availabilityForm.value) return;
  isSaving.value = true;

  try {
    // Call and await the form's saveAvailability method
    await availabilityForm.value.saveAvailability();
  } catch (err) {
    console.error("Availability save failed:", err);
    toast.error("Failed to save availability", { position: "top-right" });
  } finally {
    // Re-enable button after save (success or failure)
    isSaving.value = false;
  }
}


function onSaved() {
  showModal.value = false;
  toast.success("Availability saved successfully", { position: "top-right" });

  // Reset form after successful save
  if (availabilityForm.value) {
    availabilityForm.value.resetForm();
  }

  fetchAvailability(); // refresh table
}

function onSaveError(err) {
  console.error("Availability save error event:", err);
  const errorMessage = err?.message || err?.response?.data?.error || "Failed to save availability";
  
  // If error is about missing IDs, show a more helpful message
  if (errorMessage.includes("Availability ID") || errorMessage.includes("Recurrence ID")) {
    toast.error("Missing required IDs. Please close and reopen the form, then try again.", { 
      position: "top-right",
      duration: 5000 
    });
    // Clear the store state to force fresh fetch on next edit
    store.commit('setAvailabilityDetails', null);
  } else {
    toast.error(errorMessage, { position: "top-right" });
  }
}

function openDeleteModal(skill) {
  selectedSkill.value = skill;
  showDeleteModal.value = true;
}

async function editAvailability(id) {
  // Fetch details for this availability
  loadModal.value = true;
  const personId = getPersonId();
  const locationId = getLocationId();
  try {
    await store.dispatch('fetchAvailabilityDetails', {
      personId,
      availabilityId: id,
      locationId,
      peopleGroupId: getPeopleGroupId(),
    });

    console.log(store.getters.availabilityDetails.data.availability);
    
  } catch (err) {
    toast.error('Failed to fetch availability details', { position: 'top-right' });
  }
  loadModal.value = false;
  showModal.value = true;
}

function deleteSkill() {
  if (!selectedSkill.value) return;
  // perform delete via store action
  (async () => {
    isDeleting.value = true;
    const personId = getPersonId();
    const locationId = getLocationId();

    // availabilityId expected to be the row id
    const availabilityId = selectedSkill.value.id || selectedSkill.value.availabilityid || selectedSkill.value.availabilityId;

    // recurrence id may be present under several keys depending on API
    const recurrenceId = selectedSkill.value.recurrenceid || selectedSkill.value.recurrence_id || selectedSkill.value.recurrenceId || null;

    try {
      const res = await store.dispatch('deleteAvailability', {
        personId,
        availabilityId,
        recurrenceId,
        locationId,
        peopleGroupId: getPeopleGroupId(),
      });

      // close modal
      showDeleteModal.value = false;

      // check response for success
      const ok = res && (res.returncode === 0 || res.success || res.status === 'ok');
      if (ok) {
        const message = res?.message || 'Availability deleted successfully';
        toast.success(message, { position: 'top-right' });
      } else {
        const msg = res?.message || 'Failed to delete availability';
        toast.error(msg, { position: 'top-right' });
      }

      // refresh list
      await fetchAvailability();
    } catch (err) {
      console.error('Delete availability failed', err);
      toast.error('Failed to delete availability', { position: 'top-right' });
    } finally {
      isDeleting.value = false;
    }
  })();
}

// ✅ API Fetch with pagination & search
async function fetchAvailability(page = 1, pageSize = pagination.value.pageSize || 10) {
  const personId = getPersonId();
  const locationId = getLocationId();

  loading.value = true;
  try {
    await store.dispatch("fetchPersonAvailability", {
      personId,
      locationId,
      peopleGroupId: getPeopleGroupId(),
      search: searchTerm.value,
      page,
      pageSize,
    });
  } finally {
    loading.value = false;
  }
}

// ✅ Pagination handlers
const onChangePage = async (page) => {
  await fetchAvailability(page);
};

const onUpdatePageSize = async (size) => {
  await fetchAvailability(1, size);
};

// ✅ Search handler
const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchAvailability(1);
};

// ✅ Utility functions
function getRecurrenceLines(row) {
  return Array.isArray(row.recurrence)
    ? row.recurrence
    : row.recurrence?.split("\n") || [];
}

function sanitizeLine(raw) {
  if (raw == null) return "";
  try {
    return String(raw).replace(/^\.\s*/, "");
  } catch (e) {
    return String(raw);
  }
}

const selectedSkillName = computed(() =>
  selectedSkill.value && selectedSkill.value.name
    ? selectedSkill.value.name
    : ""
);

// 🧠 Initial Load
onMounted(async () => {
  await fetchAvailability();
});
</script>